import Oidc from "oidc-client";
import { EventEmitter } from "events";
import { Auth } from "@/common/auth";
import { Environment } from "@/common/environment";
import { AuthSettings } from "@/common/authSettings";
import Shared from "@/common/shared";

export class OidcEvents {
  public static AccessTokenExpiring = "access-token-expiring";
  public static AccessTokenExpired = "access-token-expired";
  public static SilentRenewError = "silent-renew-error";
  public static UserLoaded = "user-loaded";
  public static SessionChanged = "session-changed";
  public static UserSignedOut = "user-signed-out";
  public static UserUnloaded = "user-unloaded";
}

export default class OidcService extends EventEmitter {
  public user?: Oidc.User;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public profile!: any;
  public isLoggedIn = false;
  public manager: Oidc.UserManager = new Oidc.UserManager({});
  private auth!: Auth;

  public authSettings(environment: Environment): Auth {
    const settings: AuthSettings = environment.authSettings;

    const isSignUp = Number(localStorage.getItem(Shared.localStorageKeys.IS_SIGN_UP) || "0");

    const auth = new Auth();
    let authority = settings.loginFlow;

    if (isSignUp) {
      authority = settings.signUpFlow;
    }

    auth.ClientID = settings.clientId;
    auth.Authority = authority;
    auth.PasswordResetFlowName = settings.passwordResetFlow;
    auth.Scope = settings.scopes;

    return auth;
  }

  public initialiseManager(environment: Environment) {
    const auth = this.authSettings(environment);

    this.manager = new Oidc.UserManager({
      userStore: new Oidc.WebStorageStateStore({}),
      authority: auth.Authority,
      client_id: auth.ClientID, // eslint-disable-line @typescript-eslint/camelcase
      redirect_uri: window.location.origin + "/callback", // eslint-disable-line @typescript-eslint/camelcase
      response_type: "id_token token", // eslint-disable-line @typescript-eslint/camelcase
      scope: auth.Scope,
      post_logout_redirect_uri: window.location.origin, // eslint-disable-line @typescript-eslint/camelcase
      silent_redirect_uri: window.location.origin + "/static/silent-renew.html", // eslint-disable-line @typescript-eslint/camelcase
      accessTokenExpiringNotificationTime: 180,
      automaticSilentRenew: true,
      filterProtocolClaims: true,
      loadUserInfo: false,
    });

    this.manager.events.addAccessTokenExpiring((ev) => {
      this.emit(OidcEvents.AccessTokenExpiring, ev);
    });

    this.manager.events.addAccessTokenExpired((ev) => {
      this.emit(OidcEvents.AccessTokenExpired, ev);
    });

    this.manager.events.addSilentRenewError((ev) => {
      this.emit(OidcEvents.SilentRenewError, ev);
    });

    this.manager.events.addUserLoaded((ev) => {
      this.emit(OidcEvents.UserLoaded, ev);
    });

    this.manager.events.addUserSessionChanged(() => {
      this.emit(OidcEvents.SessionChanged);
    });

    this.manager.events.addUserSignedOut(() => {
      this.emit(OidcEvents.UserSignedOut);
    });

    this.manager.events.addUserUnloaded(() => {
      this.emit(OidcEvents.UserUnloaded);
    });

    this.auth = auth;

    const user = this.getUserFromStorage();
    if (user) {
      this.user = user;
      this.profile = user.profile;
      this.isLoggedIn = true;
    }
  }

  public async getAccessToken() {
    const user = await this.getUser();
    if (user) {
      return user.access_token;
    } else {
      return "";
    }
  }

  public renewToken() {
    const target = window.location.pathname;
    return new Promise((resolve, reject) => {
      this.manager
        .signinSilent()
        .then((user) => this.processUser(user, resolve, reject, target))
        .catch((err) => {
          // console.log(err);
          return reject(err);
        });
    });
  }

  public handleRedirectCallback() {
    return new Promise<Oidc.User>((resolve, reject) => {
      this.manager
        .signinRedirectCallback()
        .then((user) => {
          return resolve(user);
        })
        .catch((err) => {
          if (err.error_description.startsWith("AADB2C90118")) {
            this.forgotPassword();
          } else {
            return reject(err);
          }
        });
    });
  }

  public signIn() {
    this.manager.signinRedirect().catch(() => {
      // console.log(err);
    });
  }

  public forgotPassword() {
    localStorage.setItem("forgotpassword", "true");
    this.manager
      .signinRedirect({
        extraQueryParams: {
          p: this.auth.PasswordResetFlowName,
        },
      })
      .catch(() => {
        // console.log(err);
      });
  }

  public signOut() {
    this.user = undefined;
    this.profile = undefined;
    this.isLoggedIn = false;

    this.manager
      .signoutRedirect()
      .then(() => {
        // console.log('signed out', resp);
      })
      .catch(() => {
        // console.log(err);
      });
  }

  public getUser(target?: string): Promise<Oidc.User> {
    return new Promise((resolve, reject) => {
      if (!this.user) {
        // if we don't have a user, try load it from storage
        this.user = this.getUserFromStorage();
      }
      if (this.user && !this.user.expired) {
        // if we have a user and it's still valid, return
        return resolve(this.user);
      } else {
        this.manager
          .getUser()
          .then((user) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return this.processUser(user, resolve as any, reject, target);
          })
          .catch((err) => {
            // console.log(err);
            return reject(err);
          });
      }
    });
  }

  public GetUserEmail(): string {
    const emails = this.profile ? (this.profile.emails as string[]) : [];
    return emails.length ? emails[0] : "";
  }

  private getUserFromStorage(): Oidc.User | undefined {
    if (!this.auth) {
      throw new Error(
        "getUserFromStorage was called before the manager was initialized. Please initialize the manager first.",
      );
    }
    const userString = localStorage.getItem(`oidc.user:${this.auth.Authority}:${this.auth.ClientID}`);
    if (userString !== null) {
      const user = JSON.parse(userString);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (user as any).__proto__ = Oidc.User.prototype;
      return user;
    } else {
      return undefined;
    }
  }

  private processUser(
    user: Oidc.User | null,
    resolve: (value?: {} | PromiseLike<{}> | undefined) => void,
    reject: (reason?: any) => void, // eslint-disable-line @typescript-eslint/no-explicit-any
    target?: string,
  ) {
    if (!target) {
      target = window.location.pathname;
    }
    if (user == null) {
      localStorage.setItem("target", target);
      return resolve();
    } else {
      this.user = user;
      this.profile = user.profile;
      return resolve(user);
    }
  }
}
